import type { Document } from '@lmc/ts-common'
import { Add, Button, FormCard, Modal, theme, Warning } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { type CSSProperties, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DocumentItem } from '../generic'

interface CarePlanDocumentsProps {
  carePlan: { documents?: Array<string> }
  documents?: Document[]
  onCreate?: () => void
  onDelete?: (documentId: string) => void
  onEdit?: (document: Document) => void
  style?: CSSProperties
}

export default function CarePlanDocuments({
  carePlan,
  documents,
  onCreate,
  onDelete,
  onEdit,
  style
}: CarePlanDocumentsProps) {
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)

  const openDialog = useCallback((id: string) => setDocumentToDelete(id), [])

  const closeDialog = useCallback(() => setDocumentToDelete(null), [])

  const handleDelete = useCallback(() => {
    if (onDelete && documentToDelete) {
      onDelete(documentToDelete)
    }
    closeDialog()
  }, [onDelete, documentToDelete, closeDialog])

  const carePlanDocuments = useMemo(() => {
    const ids = carePlan?.documents
    if (!ids?.length || !documents) return []
    return documents.filter((doc) => ids.includes(doc.id))
  }, [carePlan?.documents, documents])

  if (!carePlan || !documents) return null

  const hasCarePlanDocuments = carePlanDocuments.length > 0

  return (
    <div style={style}>
      <FormCard
        title="Documents"
        rightChildren={
          onCreate && (
            <Button
              type="button"
              variant="text"
              icon={<Add />}
              onClick={() => onCreate()}
            >
              Upload Documents
            </Button>
          )
        }
      >
        {hasCarePlanDocuments
          ? carePlanDocuments.map((document) => (
              <DocumentItem
                data={document}
                deleteLabel="Remove"
                key={document.id}
                onDelete={openDialog}
                onEdit={onEdit as unknown as (data: { id: string }) => void}
                readOnly={!onEdit}
                type={onEdit ? 'edit' : 'create'}
              />
            ))
          : null}
      </FormCard>
      <DeleteConfirmModal
        closeButton
        closeOnConfirm={false}
        confirmButtonProps={{ variant: 'danger' }}
        isModalVisible={!!documentToDelete}
        setIsModalVisible={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
        localisation={{
          title: (
            <TitleRow>
              <Warning color={theme.color.status.warning} size={24} />
              <span>Are you sure?</span>
            </TitleRow>
          ),
          confirmText: 'Remove',
          cancelText: 'Cancel'
        }}
        onCancel={closeDialog}
        onConfirm={handleDelete}
        testId="care-plan-document-delete"
      >
        <ModalBody>
          <Text level={1} weight="regular">
            Are you sure you want to remove this document? It will no longer be
            linked to the active care plan.
          </Text>
        </ModalBody>
      </DeleteConfirmModal>
    </div>
  )
}

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const ModalBody = styled.div`
  padding: 16px 0;
`

const DeleteConfirmModal = styled(Modal)`
  min-width: 480px;
`
